import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { Button, cn, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { AdminWorkspaceListItem } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { RequirePermission } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { CalendarIcon, MembersIcon, PlusIcon, TextIcon, WorkspacesIcon } from '@/components/layout/icons';
import { FilterBar } from '@/components/filter-bar';
import { ListEmptyState, TestWorkspacesPill, WorkspaceLabel } from '@/components/test-workspaces';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';
import { SEARCH_PARAM, useListParams } from '@/lib/list-view/use-list-params';

export const workspacesLoaderParams = [] as const;

export type WorkspacesLoaderData = {
  readonly workspaces: ReadonlyArray<AdminWorkspaceListItem>;
  readonly columnPreference: ColumnPreference;
};

export async function workspacesLoader(): Promise<WorkspacesLoaderData> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminWorkspaces.list({ query: includeTestWorkspacesQuery() }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.workspaces),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load workspaces', { status: result.status });
  }

  return { workspaces: result.body.data, columnPreference };
}

function matches(workspace: AdminWorkspaceListItem, query: string): boolean {
  if (query === '') return true;
  const haystack = [
    workspace.name,
    ...(workspace.owner === null ? [] : [workspace.owner.firstName, workspace.owner.lastName, workspace.owner.email]),
  ]
    .join(' ')
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function ownerLabel(workspace: AdminWorkspaceListItem): string {
  if (workspace.owner === null) return '—';

  const name = `${workspace.owner.firstName} ${workspace.owner.lastName}`.trim();

  return name === '' ? workspace.owner.email : `${name} (${workspace.owner.email})`;
}

function useWorkspaceColumns(): ReadonlyArray<OperatorColumn<AdminWorkspaceListItem>> {
  const t = useTranslations('pages.admin.workspaces');
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        cell: (workspace) => <WorkspaceLabel isTest={workspace.isTest}>{workspace.name}</WorkspaceLabel>,
        sortValue: (workspace) => workspace.name,
      },
      {
        id: 'owner',
        label: t('columns.owner'),
        icon: MembersIcon,
        cell: ownerLabel,
        sortValue: ownerLabel,
      },
      {
        id: 'createdAt',
        label: t('columns.created-at'),
        icon: CalendarIcon,
        headClassName: 'w-[150px]',
        cellClassName: 'text-text-tertiary',
        cell: (workspace) => intl.formatDate(workspace.createdAt, { dateStyle: 'short' }),
        sortValue: (workspace) => Date.parse(workspace.createdAt),
      },
    ],
    [t, intl],
  );
}

export function WorkspacesPage() {
  const t = useTranslations('pages.admin.workspaces');
  const tFilters = useTranslations('pages.admin.shell.filters');
  const { workspaces, columnPreference } = useLoaderData() as WorkspacesLoaderData;
  const navigate = useNavigate();
  const location = useLocation();

  const { params, setParams } = useListParams();
  const query = params.get(SEARCH_PARAM) ?? '';
  const filtered = useMemo(() => workspaces.filter((w) => matches(w, query.trim())), [workspaces, query]);
  const catalogue = useWorkspaceColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.workspaces,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar
        leading={<ListLeading title={t('title')} count={filtered.length} icon={WorkspacesIcon} />}
        trailing={
          <RequirePermission permission="backoffice:identity:workspace-create">
            <Button size="sm" onClick={() => navigate({ pathname: '/workspaces/new', search: location.search })}>
              <PlusIcon className="h-4 w-4" />
              {t('create-cta')}
            </Button>
          </RequirePermission>
        }
      />
      <FilterBar
        pills={<TestWorkspacesPill />}
        searchValue={query}
        searchPlaceholder={tFilters('search')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      {filtered.length === 0 ? (
        <ListEmptyState title={t('empty')} serverEmpty={workspaces.length === 0} />
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((workspace) => (
              <TableRow key={workspace.id} interactive onClick={() => navigate(`/workspaces/${workspace.id}`)}>
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(workspace)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Outlet />
    </>
  );
}
